import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router';
import classNames from 'classnames';

import lang from '../../../lib/language';
import { ReportingCall } from '../../../lib/models/reporting/types';
import { RootState } from '../../../lib/store/index';
import { OpenPosition, PositionType } from '../../../lib/store/reporting/types';
import TradePriceCache from '../../../lib/store-util/TradePriceCache';
import {
  calcUnrealizedProfitPercent,
  formatCurrency,
  formatNumberWithSuffix,
  roundUnrealizedProfit,
  unrealizedPnL,
} from '../../../lib/util/DataHelpers';
import { isCallStatusReady } from '../../../lib/util/error-handling/StatusByCallHelpers';

import { useIsMobile } from '../../../hooks';
import Button from '../../../shared_components/Button/Button';
import { ButtonTypesEnum } from '../../../shared_components/Button/Button.types';
import { UI_ROUTES } from '../../../util/routes';

import styles from './SharesOwned.module.scss';

type Props = ReduxProps & {
  symbol: string;
};

const SharesOwned = ({
  symbol,
  positions,
  tradeHistoryOrders,
  isOpenPositionsReady,
  isTradeHistoryReady,
}: Props) => {
  const { isMobile } = useIsMobile();
  const navigate = useNavigate();
  const tradePricesCache = TradePriceCache.use(symbol, 'SharesOwned');
  const marketPrice = tradePricesCache.currentPrice;
  const symbolPosition = positions.find((el) => el.symbol === symbol);

  const unrealizedPrice = (position: OpenPosition) => {
    const { quantity, averageOpeningPrice } = position;
    if (marketPrice) {
      return roundUnrealizedProfit(unrealizedPnL(quantity, marketPrice, averageOpeningPrice), true);
    }
    return '-';
  };

  const getPriceChange = (position: OpenPosition) => {
    const { quantity, averageOpeningPrice } = position;
    if (marketPrice) {
      return unrealizedPnL(quantity, marketPrice, averageOpeningPrice) ?? 0;
    }
    return 0;
  };

  const checkPosition = isOpenPositionsReady && positions.find((el) => el.symbol === symbol);
  const checkHistoryOrders = isTradeHistoryReady && tradeHistoryOrders.find((el) => el.symbol === symbol);

  const renderViewHistoryBtn = (
    <Button
      className={styles.viewHistoryButton}
      variant={ButtonTypesEnum.Link}
      onClick={() =>
        navigate(
          {
            pathname: `${UI_ROUTES.tradeHistory}`,
          },
          { state: { symbol } },
        )}
    >
      {lang.commonViewHistory()}
    </Button>
  );

  const unrealizedProfitPercent = (order: OpenPosition) =>
    calcUnrealizedProfitPercent(order.positionType, marketPrice!, order.averageOpeningPrice);

  if (checkHistoryOrders && !checkPosition) {
    return (
      <div className={styles.wrapperMessage}>
        <span className={styles.message}>{lang.commonYouHaveTraded()}</span>
        {renderViewHistoryBtn}
      </div>
    );
  }

  return (
    <>
      {checkPosition && (
        <div className={styles.wrapperOwnedShares}>
          <div className={isMobile ? styles.headerLineMobile : styles.headerLine}>
            <div className={styles.wrapperTitle}>
              <span className={isMobile ? styles.titleMobile : styles.title}>
                {lang.commonSharesOwned()}
                {symbolPosition?.positionType === PositionType.Sell && (
                  <span className={styles.shortTitle}>{lang.commonShort()}</span>
                )}
              </span>
              {renderViewHistoryBtn}
            </div>
          </div>
          <div className={isMobile ? styles.wrapperMobile : styles.wrapper}>
            {symbolPosition && (
              <>
                <div
                  className={
                    isMobile
                      ? classNames({
                        [styles.cardMobile]: symbolPosition,
                        [styles.bottomMargin]: !symbolPosition && isMobile,
                      })
                      : styles.card
                  }
                >
                  {symbolPosition ? (
                    <div className={isMobile ? styles.cardInnerMobile : styles.cardInner}>
                      <div className={styles.cardContent}>
                        <p>{lang.headerAccountBreakdownQuantity()}</p>
                        <p className={styles.valueContent}>{Math.abs(symbolPosition.quantity)}</p>
                      </div>
                      <div className={styles.cardContent}>
                        <p>{lang.positionAverageOpenPrice()}</p>
                        <p className={styles.valueContent}>
                          {formatCurrency(symbolPosition.averageOpeningPrice)}
                        </p>
                      </div>
                      <div className={styles.cardContent}>
                        <p>{lang.headerAccountBreakdownOpenValue()}</p>
                        <p className={styles.valueContent}>{formatCurrency(symbolPosition.cost)}</p>
                      </div>
                    </div>
                  ) : (
                    <div className={styles.loader} />
                  )}
                </div>
                <div
                  className={
                    isMobile ? classNames({ [styles.cardMobile]: symbolPosition }) : styles.card
                  }
                >
                  {symbolPosition ? (
                    <div className={isMobile ? styles.cardInnerMobile : styles.cardInner}>
                      <div className={styles.cardContent}>
                        <p>{lang.positionAUnrealisedProfitValue()}</p>
                        <p
                          className={classNames(styles.valueContent, {
                            [styles.valueContentPositive]: getPriceChange(symbolPosition) > 0,
                            [styles.valueContentNegative]: getPriceChange(symbolPosition) < 0,
                          })}
                        >
                          {unrealizedPrice(symbolPosition)}
                        </p>
                      </div>
                      <div className={styles.cardContent}>
                        <p>{lang.positionAUnrealisedProfitPercent()}</p>
                        <p
                          className={classNames(styles.valueContent, {
                            [styles.valueContentPositive]: getPriceChange(symbolPosition) > 0,
                            [styles.valueContentNegative]: getPriceChange(symbolPosition) < 0,
                          })}
                        >
                          {marketPrice ? (
                            <span className={styles.valueContent}>
                              {formatNumberWithSuffix(unrealizedProfitPercent(symbolPosition), '%')}
                            </span>
                          ) : (
                            '-'
                          )}
                        </p>
                      </div>
                      <div className={styles.cardContent}>
                        <p>{lang.headerAccountBreakdownMarketValue()}</p>
                        <p className={styles.valueContent}>
                          {marketPrice
                            ? formatCurrency(Math.abs(symbolPosition.quantity) * marketPrice)
                            : '-'}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className={styles.loader} />
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  positions: state.reporting.openPositions,
  tradeHistoryOrders: state.reporting.tradeHistoryOrders,
  isOpenPositionsReady: isCallStatusReady(
    ReportingCall.getOpenPositions,
    state.reporting.statusByCall,
  ),
  isTradeHistoryReady: isCallStatusReady(
    ReportingCall.getAccountTradeHistory,
    state.reporting.statusByCall,
  ),
  accountOpenPositionsCompleted: state.reporting.accountOpenPositionsCompleted,
});

const connector = connect(mapStateToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(SharesOwned);
